import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  MapPin,
  Radio,
  FileText,
  Send,
  AlertTriangle,
  Plus,
  X,
  Loader2,
} from 'lucide-react';
import { cn, formatTimestamp, formatDateTime, getSeverityColor, getSeverityBg, getStatusColor, getCategoryIcon, API_BASE_URL } from '../lib/utils';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:4040';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '../store';
import { sendMdtDispatch, cancelDispatch, respondToDispatch } from '../store/slices/dispatchSlice';
import { useActiveEvent } from '../contexts/EventContext';
import MapDrawingTools from '../components/map/MapDrawingTools';
import { io } from 'socket.io-client';
import type { Incident, Unit, MdtDispatch, IncidentLog, ManualPatientType } from '../types';
import { MANUAL_PATIENT_LIST_LABELS } from '../lib/manualPatients';
import { submitOrQueue } from '../lib/resilientApi';

export default function IncidentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAppSelector((state) => state.auth);
  const reduxDispatch = useAppDispatch();
  const { dispatches } = useAppSelector((state) => state.dispatch);
  const { activeEventId, activeEvent } = useActiveEvent();
  const eventLocked = activeEvent?.status === 'closed';

  const [incident, setIncident] = useState<Incident | null>(null);
  const [loading, setLoading] = useState(true);
  const [newLogEntry, setNewLogEntry] = useState('');
  const [addingLog, setAddingLog] = useState(false);
  const logInputRef = useRef<HTMLInputElement | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [prfs, setPrfs] = useState<Array<{ id: string; incidentId: string; eventId: string; submittedByName: string; submittedAt: string }>>([]);

  // Inline add state
  const [addingUnit, setAddingUnit] = useState(false);
  const [newUnitName, setNewUnitName] = useState('');
  const [showShortcodeHelp, setShowShortcodeHelp] = useState(false);
  const [onlineResourceIds, setOnlineResourceIds] = useState<Set<string>>(new Set());
  const [trackingData, setTrackingData] = useState<Array<{ resourceId: string; resourceName: string; status: string; timestamp: string }>>([]);
  // Confirmation state for creating a new unit (manual add or AV/ shortcode)
  // Holds the uppercase call sign awaiting a second Enter to confirm creation.
  const [pendingNewUnit, setPendingNewUnit] = useState<string | null>(null);
  const [pendingNewUnitSource, setPendingNewUnitSource] = useState<'manual' | 'shortcode' | null>(null);
  // Close confirmation when units are still assigned
  const [pendingClose, setPendingClose] = useState<{ reason: string } | null>(null);
  const [closingBusy, setClosingBusy] = useState(false);

  const fetchIncident = useCallback(async () => {
    if (!token || !id) return;
    try {
      const res = await fetch(`${API_BASE_URL}/incidents/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setIncident(data.incident);
      }
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, [token, id]);

  const fetchUnits = useCallback(async () => {
    try {
      const unitsUrl = activeEventId
        ? `${API_BASE_URL}/units?eventId=${activeEventId}`
        : `${API_BASE_URL}/units`;
      const res = await fetch(unitsUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUnits(data.data || []);
      }
    } catch { /* ignore */ }
  }, [token, activeEventId]);

  const fetchPrfs = useCallback(async () => {
    if (!token || !id) return;
    try {
      const res = await fetch(`${API_BASE_URL}/prfs?incidentId=${encodeURIComponent(id)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPrfs(data.prfs || []);
      }
    } catch { /* ignore */ }
  }, [token, id]);

  useEffect(() => { fetchIncident(); fetchUnits(); fetchPrfs(); }, [fetchIncident, fetchUnits, fetchPrfs]);

  // Poll MDT tracking to know which resources are online (recent ping < 30s) and their status
  useEffect(() => {
    if (!token) return;
    const poll = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/tracking`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          const now = Date.now();
          const online = new Set<string>();
          const tracking: Array<{ resourceId: string; resourceName: string; status: string; timestamp: string }> = [];
          (data.positions || []).forEach((p: { resourceId: string; resourceName: string; status: string; timestamp: string }) => {
            if (p.resourceId && now - new Date(p.timestamp).getTime() < 30000) {
              online.add(p.resourceId);
              tracking.push(p);
            }
          });
          setOnlineResourceIds(online);
          setTrackingData(tracking);
        }
      } catch { /* ignore */ }
    };
    poll();
    const t = setInterval(poll, 10000);
    return () => clearInterval(t);
  }, [token]);

  // Refresh PRF list when window regains focus (e.g. after submitting on MDT tab)
  useEffect(() => {
    const onFocus = () => fetchPrfs();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchPrfs]);

  // Live updates via socket.io: refetch when this incident or its PRFs change
  useEffect(() => {
    if (!id) return;
    const token = localStorage.getItem('c2_token');
    const socket = io(SOCKET_URL, { transports: ['websocket'], auth: { token } });
    const onIncidentUpdate = (updated: { id: string }) => {
      if (updated.id === id) fetchIncident();
    };
    const onPrfCreated = (prf: { incidentId: string }) => {
      if (prf.incidentId === id) fetchPrfs();
    };
    socket.on('incident:updated', onIncidentUpdate);
    socket.on('prf:created', onPrfCreated);
    return () => {
      socket.off('incident:updated', onIncidentUpdate);
      socket.off('prf:created', onPrfCreated);
      socket.disconnect();
    };
  }, [id, fetchIncident, fetchPrfs]);

  const appendLocalLog = (details: string, action?: string) => {
    if (!id || !incident) return;
    const displayName =
      user?.username || (user?.firstName ? `${user.firstName} ${user.lastName}` : user?.email) || 'You';
    const log: IncidentLog = {
      id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      incidentId: id,
      userId: user?.id || '',
      userName: displayName,
      action: action || 'Note',
      details,
      timestamp: new Date().toISOString(),
    };
    setIncident({ ...incident, logs: [...(incident.logs || []), log] });
  };

  const patchIncident = async (updates: Partial<Incident>) => {
    if (!token || !id || !incident) return;
    setIncident({ ...incident, ...updates, updatedAt: new Date().toISOString() });
    const result = await submitOrQueue({
      method: 'PATCH',
      path: `/incidents/${id}`,
      body: updates,
      token,
    });
    if (!result.ok && !result.queued) {
      await fetchIncident();
    }
  };

  // Post a raw log entry to the backend (queues silently on failure)
  const postLogEntry = async (details: string, action?: string) => {
    if (!token || !id) return false;
    appendLocalLog(details, action);
    const result = await submitOrQueue({
      method: 'POST',
      path: `/incidents/${id}/logs`,
      body: { details, action, offlineQueued: true },
      token,
      queueType: 'incident_note',
      queuePayload: { incidentId: id, details, actionLabel: action || 'Note' },
    });
    if (!result.ok && !result.queued) {
      setIncident((prev) =>
        prev ? { ...prev, logs: (prev.logs || []).slice(0, -1) } : prev
      );
      return false;
    }
    return true;
  };

  // Find a unit by call sign (case-insensitive, trimmed).
  // First checks event-scoped units, then falls back to all resources if not found.
  const findUnit = async (callSign: string): Promise<Unit | undefined> => {
    const cs = callSign.trim().toLowerCase();
    const local = units.find((u) => u.callSign.toLowerCase() === cs);
    if (local) return local;

    // Fallback: fetch all resources and search
    try {
      const res = await fetch(`${API_BASE_URL}/resources`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const r = (data.resources || []).find((res: any) => res.name?.toLowerCase() === cs);
        if (r) {
          return {
            id: r.id,
            callSign: r.name,
            type: r.type,
            status: r.status,
            personnel: [],
            equipment: r.capabilities || [],
            location: r.location,
            lastUpdate: r.lastUpdate,
          };
        }
      }
    } catch { /* ignore */ }
    return undefined;
  };

  // Create a placeholder resource for a quick-assign and return the new Unit (or undefined on failure).
  // Type defaults to 'vehicle' so it appears in the unit picker for MDT dispatch.
  const createQuickResource = async (callSign: string): Promise<Unit | undefined> => {
    if (!token || !incident) return undefined;
    try {
      const res = await fetch(`${API_BASE_URL}/resources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          eventId: incident.eventId,
          name: callSign,
          type: 'vehicle',
          department: 'Other',
          capabilities: [],
        }),
      });
      if (!res.ok) return undefined;
      const data = await res.json();
      const r = data.resource;
      const newUnit: Unit = {
        id: r.id,
        callSign: r.name,
        type: r.type,
        status: r.status,
        personnel: [],
        equipment: r.capabilities || [],
        location: r.location,
        lastUpdate: r.lastUpdate || new Date().toISOString(),
      };
      // Refresh local units cache so subsequent lookups succeed
      await fetchUnits();
      return newUnit;
    } catch { return undefined; }
  };

  // Send an MDT dispatch for a unit (mirrors MdtDispatchModal behaviour)
  const sendDispatchToUnit = async (unit: Unit, note: string) => {
    if (!incident) return;
    const dispatchId = `DSP-${Date.now().toString(36).toUpperCase()}`;
    const payload: MdtDispatch = {
      id: dispatchId,
      incidentId: incident.id,
      eventId: incident.eventId,
      incidentTitle: incident.title,
      incidentSeverity: incident.severity,
      incidentCategory: incident.category,
      incidentLocation: incident.location,
      targetUnitId: unit.id,
      targetUnitCallSign: unit.callSign,
      dispatchedBy: user ? `${user.firstName} ${user.lastName}` : 'Dispatch',
      dispatchedAt: new Date().toISOString(),
      status: 'pending',
      notes: note || undefined,
    };
    reduxDispatch(sendMdtDispatch(payload));
    // Persist to backend via HTTP POST
    try {
      const res = await fetch(`${API_BASE_URL}/dispatches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        const saved = data.dispatch as MdtDispatch | undefined;
        if (saved?.status === 'accepted' && saved.respondedAt) {
          reduxDispatch(respondToDispatch({
            id: saved.id,
            status: 'accepted',
            respondedAt: saved.respondedAt,
          }));
        }
      }
    } catch { /* ignore network errors */ }
  };

  const deassignAllUnitsFromIncident = async () => {
    if (!incident || !token) return;
    const assigned = [...(incident.assignedUnits || [])];

    // Cancel pending MDT dispatches for this incident
    const pending = dispatches.filter(
      (d) => d.incidentId === incident.id && d.status === 'pending'
    );
    for (const d of pending) {
      reduxDispatch(cancelDispatch(d.id));
      try {
        await fetch(`${API_BASE_URL}/dispatches/${d.id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch { /* ignore */ }
    }

    // Clear each unit's resource assignment
    for (const callSign of assigned) {
      const unit = await findUnit(callSign);
      if (!unit) continue;
      try {
        await fetch(`${API_BASE_URL}/resources/${unit.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            assignedIncident: null,
            status: 'available',
          }),
        });
      } catch { /* ignore */ }
    }
  };

  const executeCloseIncident = async (reason: string, deassign: boolean) => {
    if (!incident || !token || !id) return;
    setClosingBusy(true);
    try {
      const assigned = [...(incident.assignedUnits || [])];
      if (deassign && assigned.length > 0) {
        await deassignAllUnitsFromIncident();
      }

      await patchIncident({
        status: 'closed',
        closedAt: new Date().toISOString(),
        ...(deassign ? { assignedUnits: [] } : {}),
      });

      if (deassign && assigned.length > 0) {
        await postLogEntry(
          `CLOSE/${reason} — Incident closed; units deassigned: ${assigned.join(', ')}`,
          'Shortcode'
        );
      } else {
        await postLogEntry(`CLOSE/${reason} — Incident closed`, 'Shortcode');
      }
    } finally {
      setClosingBusy(false);
      setPendingClose(null);
    }
  };

  const addManualPatient = async (type: ManualPatientType, notes: string): Promise<boolean> => {
    if (!incident || !token || !id) return false;
    try {
      const res = await fetch(`${API_BASE_URL}/incidents/${id}/manual-patients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ type, notes }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      if (data.incident) setIncident(data.incident);
      return true;
    } catch {
      return false;
    }
  };

  // Process a shortcode.
  //   false  = not a shortcode → treat as a normal log entry
  //   true   = handled, clear input
  //  'pending' = handled but awaiting a second Enter; keep input
  const processShortcode = async (raw: string): Promise<boolean | 'pending'> => {
    if (!incident) return false;
    const match = raw.match(/^([A-Z]{2,12})\/(.+)$/i);
    if (!match) return false;
    const cmd = match[1].toUpperCase();
    const arg = match[2].trim();
    if (!arg) return false;

    switch (cmd) {
      case 'SUP': {
        // Supervise incident - set status to supervision and add log
        const reason = arg;

        await fetch(`${API_BASE_URL}/incidents/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            status: 'supervision',
          }),
        });

        await postLogEntry(`Supervision required: ${reason}`, 'Shortcode');
        return true;
      }
      case 'CLOSE': {
        const assigned = incident.assignedUnits || [];
        if (assigned.length > 0) {
          setPendingClose({ reason: arg });
          return true;
        }
        await executeCloseIncident(arg, false);
        return true;
      }
      case 'AV': {
        const callSign = arg.toUpperCase();
        let unit = await findUnit(callSign);
        if (!unit) {
          // First attempt: ask the user to confirm by pressing Enter again
          if (pendingNewUnit !== callSign || pendingNewUnitSource !== 'shortcode') {
            setPendingNewUnit(callSign);
            setPendingNewUnitSource('shortcode');
            // Keep input populated so Enter resubmits the same shortcode
            return 'pending';
          }
          // Confirmed: create the resource on the fly
          unit = await createQuickResource(callSign);
          setPendingNewUnit(null);
          setPendingNewUnitSource(null);
          if (!unit) {
            await postLogEntry(`AV/${callSign} failed — unable to create unit`, 'Shortcode');
            return true;
          }
        }
        const online = onlineResourceIds.has(unit.id);
        const current = incident.assignedUnits || [];
        if (!current.includes(unit.callSign)) {
          await patchIncident({ assignedUnits: [...current, unit.callSign] });
        }
        sendDispatchToUnit(unit, `Assigned via AV/ shortcode on ${incident.id}`);
        await postLogEntry(
          online
            ? `AV ${unit.callSign} — assigned & dispatch sent to MDT`
            : `AV ${unit.callSign} — assigned (no active MDT, manual tracking)`,
          'Assign'
        );
        return true;
      }
      case 'DA': {
        const unit = await findUnit(arg);
        const callSign = unit ? unit.callSign : arg;
        const current = incident.assignedUnits || [];
        if (current.includes(callSign)) {
          await patchIncident({ assignedUnits: current.filter((u) => u !== callSign) });
        }
        // Cancel any pending dispatches for this unit + incident
        const pending = dispatches.filter(
          (d) => d.incidentId === incident.id && d.targetUnitCallSign.toLowerCase() === callSign.toLowerCase() && d.status === 'pending'
        );
        for (const d of pending) {
          reduxDispatch(cancelDispatch(d.id));
          try {
            await fetch(`${API_BASE_URL}/dispatches/${d.id}`, {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${token}` },
            });
          } catch { /* ignore */ }
        }
        await postLogEntry(`DA ${callSign} — unassigned${pending.length ? ' & MDT dispatch cancelled' : ''}`, 'Unassign');
        return true;
      }
      case 'EN': {
        const unit = await findUnit(arg);
        const callSign = unit ? unit.callSign : arg;
        await postLogEntry(`${callSign} en route`, 'En Route');
        return true;
      }
      case 'OS': {
        const unit = await findUnit(arg);
        const callSign = unit ? unit.callSign : arg;
        await postLogEntry(`${callSign} on scene`, 'On Scene');
        return true;
      }
      case 'CL': {
        const unit = await findUnit(arg);
        const callSign = unit ? unit.callSign : arg;
        const current = incident.assignedUnits || [];
        if (current.includes(callSign)) {
          await patchIncident({ assignedUnits: current.filter((u) => u !== callSign) });
        }
        await postLogEntry(`${callSign} cleared from incident`, 'Cleared');
        return true;
      }
      case 'STATUS': {
        const [statusPart, ...notesParts] = arg.split('/');
        const notes = notesParts.join('/').trim();
        const normalised = statusPart.toLowerCase().replace(/\s+/g, '_');
        const allowed = ['open', 'supervision', 'closed'];
        if (!allowed.includes(normalised)) {
          await postLogEntry(`STATUS/${arg} failed — invalid status. Allowed: ${allowed.join(', ')}`, 'Shortcode');
          return true;
        }
        if (normalised === 'closed' && (incident.assignedUnits || []).length > 0) {
          setPendingClose({ reason: notes || 'closed' });
          return true;
        }
        await patchIncident({ status: normalised as Incident['status'] });
        const logDetails = notes ? `Status set to ${normalised} — ${notes}` : `Status set to ${normalised}`;
        await postLogEntry(logDetails, 'Status');
        return true;
      }
      case 'COMMANDER': {
        await patchIncident({ commander: arg } as Partial<Incident>);
        await postLogEntry(`Commander set to ${arg}`, 'Commander');
        return true;
      }
      case 'TOA': {
        const unit = await findUnit(arg);
        const callSign = unit ? unit.callSign : arg.toUpperCase();
        const arrivedAt = new Date().toISOString();
        await postLogEntry(`${callSign} time of arrival — ${formatDateTime(arrivedAt)}`, 'Time of Arrival');
        return true;
      }
      case 'PX': {
        const pxMatch = arg.match(/^(MI|PRF|PAR)\/(.+)$/i);
        if (!pxMatch) {
          await postLogEntry('PX/ failed — use PX/MI/, PX/PRF/, or PX/PAR/ followed by notes', 'Shortcode');
          return true;
        }
        const type = pxMatch[1].toUpperCase() as ManualPatientType;
        const notes = pxMatch[2].trim();
        if (!notes) {
          await postLogEntry(`PX/${type}/ failed — notes required after form type`, 'Shortcode');
          return true;
        }
        const saved = await addManualPatient(type, notes);
        if (!saved) {
          await postLogEntry(`PX/${type}/ failed — could not save paper patient record`, 'Shortcode');
          return true;
        }
        await postLogEntry(`Paper ${MANUAL_PATIENT_LIST_LABELS[type]} recorded — ${notes}`, 'Paper Patient');
        return true;
      }
      case 'TAG': {
        const tag = arg.trim();
        const current = incident.tags || [];
        if (!current.includes(tag)) {
          await patchIncident({ tags: [...current, tag] });
        }
        await postLogEntry(`Tag added: ${tag}`, 'Tag');
        return true;
      }
      default:
        return false;
    }
  };

  const handleAddLog = async () => {
    if (!newLogEntry.trim() || !token || !id || eventLocked) return;
    setAddingLog(true);
    try {
      const raw = newLogEntry.trim();
      const handled = await processShortcode(raw);
      if (!handled) {
        await postLogEntry(raw);
      } else if (handled === true) {
        await fetchIncident();
      }
      // Keep input populated when awaiting a second-Enter confirmation
      if (handled !== 'pending') {
        setNewLogEntry('');
      }
    } finally {
      setAddingLog(false);
      // Return focus to the log input for continuous note entry
      setTimeout(() => logInputRef.current?.focus(), 0);
    }
  };

  const handleAddUnit = async () => {
    if (!newUnitName.trim() || !incident) return;
    const callSign = newUnitName.trim().toUpperCase();
    const existing = await findUnit(callSign);

    if (!existing) {
      // First Enter on an unknown unit: ask for confirmation
      if (pendingNewUnit !== callSign || pendingNewUnitSource !== 'manual') {
        setPendingNewUnit(callSign);
        setPendingNewUnitSource('manual');
        setNewUnitName(callSign); // capitalise the input visually
        return;
      }
      // Second Enter: create the resource
      const created = await createQuickResource(callSign);
      setPendingNewUnit(null);
      setPendingNewUnitSource(null);
      if (!created) return; // creation failed; leave field for retry
    }

    const current = incident.assignedUnits || [];
    if (!current.includes(callSign)) {
      await patchIncident({ assignedUnits: [...current, callSign] });
    }
    setNewUnitName('');
    setAddingUnit(false);
  };

  const handleRemoveUnit = (unit: string) => {
    if (!incident) return;
    const updated = (incident.assignedUnits || []).filter((u) => u !== unit);
    patchIncident({ assignedUnits: updated });
  };

  const mapIncidents = useMemo(() => (incident ? [incident] : []), [incident]);
  const mapCenter = useMemo((): [number, number] | undefined => {
    if (incident?.location?.latitude == null || incident?.location?.longitude == null) return undefined;
    return [incident.location.latitude, incident.location.longitude];
  }, [incident?.location?.latitude, incident?.location?.longitude]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!incident) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3">
        <AlertTriangle className="w-8 h-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Incident {id} not found</p>
        <button onClick={() => navigate(-1)} className="text-xs text-primary hover:underline">Go back</button>
      </div>
    );
  }

  const assignedOnClose = pendingClose ? (incident.assignedUnits || []) : [];

  return (
    <div className="h-full flex flex-col gap-3">
      {eventLocked && (
        <div className="flex-shrink-0 px-3 py-2 rounded-lg border border-gray-500/30 bg-gray-500/10 text-[11px] text-gray-300 flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-gray-400" />
          Event is closed — incident editing is locked. Reopen the event to make changes.
        </div>
      )}
      {/* Close confirmation — units still assigned */}
      {pendingClose && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="w-full max-w-md rounded-2xl p-5 space-y-4 border border-border bg-card shadow-xl">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-foreground">Units still assigned</h3>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  This incident still has {assignedOnClose.length} unit{assignedOnClose.length === 1 ? '' : 's'} assigned
                  ({assignedOnClose.join(', ')}). Closing without deassigning leaves them tied to a closed job.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <button
                type="button"
                disabled={closingBusy}
                onClick={() => setPendingClose(null)}
                className="flex-1 py-2.5 rounded-xl text-xs font-bold border border-border bg-background text-foreground hover:bg-accent transition-colors disabled:opacity-50"
              >
                Cancel close
              </button>
              <button
                type="button"
                disabled={closingBusy}
                onClick={() => executeCloseIncident(pendingClose.reason, true)}
                className="flex-1 py-2.5 rounded-xl text-xs font-bold border border-red-500/40 bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-colors disabled:opacity-50"
              >
                {closingBusy ? 'Closing…' : 'Close and deassign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header - fixed */}
      <div className="flex items-center gap-3 flex-shrink-0">
        <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-accent transition-colors">
          <ArrowLeft className="w-4 h-4 text-muted-foreground" />
        </button>
        <span className="text-base">{getCategoryIcon(incident.category)}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="text-[10px] font-mono text-muted-foreground">{incident.id}</span>
            <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full border font-medium', getSeverityBg(incident.severity), getSeverityColor(incident.severity))}>
              {incident.severity.toUpperCase()}
            </span>
            <span className={cn('text-[10px] font-medium', getStatusColor(incident.status))}>
              {incident.status.replace('_', ' ').toUpperCase()}
            </span>
            {(() => {
              const isClosed = incident.status === 'closed';
              const isSupervision = incident.status === 'supervision';
              const lifecycleLabel = isClosed ? 'CLOSED' : isSupervision ? 'SUPERVISION' : 'OPEN';
              const lifecycleClass = isClosed
                ? 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                : isSupervision
                  ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                  : 'bg-green-500/20 text-green-400 border-green-500/30';
              return (
                <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full border font-medium', lifecycleClass)}>
                  {lifecycleLabel}
                </span>
              );
            })()}
          </div>
          <h1 className="text-sm font-bold text-foreground truncate">{incident.title}</h1>
        </div>
      </div>

      {/* Content grid - fills remaining space */}
      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Left Column: Location + Map + Units */}
        <div className="flex flex-col gap-3 min-h-0 overflow-y-auto">
          {/* Location */}
          <div className="glass-panel p-3 flex-shrink-0">
            <div className="flex items-start gap-2">
              <MapPin className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-foreground leading-snug">{incident.location?.address || 'No address'}</p>
                {incident.location?.latitude && incident.location?.longitude && (
                  <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">
                    {incident.location.latitude.toFixed(4)}, {incident.location.longitude.toFixed(4)}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="glass-panel overflow-hidden flex-shrink-0 relative" style={{ height: '280px' }}>
            {incident.location?.latitude && incident.location?.longitude ? (
              <MapDrawingTools
                incidents={mapIncidents}
                center={mapCenter}
                zoom={17}
                className="h-full"
              />
            ) : (
              <div className="h-full flex items-center justify-center text-[10px] text-muted-foreground">
                No location available
              </div>
            )}
          </div>

          {/* Assigned Units */}
          <div className="glass-panel p-3 flex-shrink-0">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Radio className="w-3 h-3" /> Units ({(incident.assignedUnits || []).length})
              </h3>
              <button onClick={() => setAddingUnit(true)} className="p-0.5 rounded hover:bg-accent transition-colors" title="Add unit">
                <Plus className="w-3.5 h-3.5 text-primary" />
              </button>
            </div>
            {addingUnit && (
              <div className="mb-2">
                <div className="flex gap-1">
                  <input
                    value={newUnitName}
                    onChange={(e) => {
                      const v = e.target.value.toUpperCase();
                      setNewUnitName(v);
                      if (pendingNewUnitSource === 'manual' && v !== pendingNewUnit) {
                        setPendingNewUnit(null);
                        setPendingNewUnitSource(null);
                      }
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddUnit()}
                    placeholder="Unit call sign..."
                    className="flex-1 px-2 py-1 rounded bg-background border border-border text-xs text-foreground uppercase focus:outline-none focus:ring-1 focus:ring-primary/50"
                    autoFocus
                  />
                  <button onClick={handleAddUnit} className="p-1 rounded bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                    <Plus className="w-3 h-3" />
                  </button>
                  <button onClick={() => { setAddingUnit(false); setNewUnitName(''); setPendingNewUnit(null); setPendingNewUnitSource(null); }} className="p-1 rounded hover:bg-accent transition-colors">
                    <X className="w-3 h-3 text-muted-foreground" />
                  </button>
                </div>
                {pendingNewUnitSource === 'manual' && pendingNewUnit && (
                  <p className="text-[10px] text-amber-400 mt-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Unit <span className="font-mono font-bold">{pendingNewUnit}</span> does not exist. Press Enter again to create &amp; assign.
                  </p>
                )}
              </div>
            )}
            <div className="space-y-1">
              {(incident.assignedUnits || []).map((unit) => {
                const tracking = trackingData.find(t => t.resourceName === unit || t.resourceId === unit);
                const status = tracking?.status || 'unknown';
                const statusColors: Record<string, { bg: string; text: string; label: string }> = {
                  available: { bg: 'bg-emerald-400/20', text: 'text-emerald-400', label: 'Available' },
                  busy: { bg: 'bg-amber-400/20', text: 'text-amber-400', label: 'Busy' },
                  enroute: { bg: 'bg-cyan-400/20', text: 'text-cyan-400', label: 'En Route' },
                  onscene: { bg: 'bg-red-400/20', text: 'text-red-400', label: 'On Scene' },
                  onbreak: { bg: 'bg-purple-400/20', text: 'text-purple-400', label: 'On Break' },
                  unknown: { bg: 'bg-gray-400/20', text: 'text-gray-400', label: 'Unknown' },
                };
                const statusStyle = statusColors[status] || statusColors.unknown;
                const isOnline = tracking ? onlineResourceIds.has(tracking.resourceId) : false;
                return (
                  <div key={unit} className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-accent/50 group">
                    <div className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-green-400' : 'bg-gray-400'}`} />
                    <span className="text-xs text-foreground font-mono flex-1">{unit}</span>
                    {tracking && (
                      <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${statusStyle.bg} ${statusStyle.text}`}>
                        {statusStyle.label}
                      </span>
                    )}
                    <button onClick={() => handleRemoveUnit(unit)} className="p-0.5 rounded hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-all" title="Remove unit">
                      <X className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                    </button>
                  </div>
                );
              })}
              {(incident.assignedUnits || []).length === 0 && (
                <p className="text-[10px] text-muted-foreground text-center py-2">No units assigned</p>
              )}
            </div>
          </div>

          {/* Patient records — digital PRFs and paper/off-network forms */}
          {((incident.manualPatients || []).length > 0 || prfs.length > 0) && (
            <div className="glass-panel p-3 flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <FileText className="w-3 h-3" /> Patients ({prfs.length + (incident.manualPatients || []).length})
                </h3>
              </div>
              <div className="space-y-1.5">
                {prfs.map((prf) => (
                  <div key={prf.id} className="flex items-start gap-2 px-2 py-1.5 rounded-lg bg-accent/50 border border-border/50">
                    <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-3 h-3 text-emerald-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-[11px] font-mono font-semibold text-foreground truncate" title={prf.id}>{prf.id}</p>
                        <span className="text-[8px] px-1 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-semibold">Digital</span>
                      </div>
                      <p className="text-[9px] text-muted-foreground truncate">
                        {prf.submittedByName} &bull; {formatTimestamp(prf.submittedAt)}
                      </p>
                    </div>
                  </div>
                ))}
                {(incident.manualPatients || []).map((record) => (
                  <div key={record.id} className="flex items-start gap-2 px-2 py-1.5 rounded-lg bg-amber-500/5 border border-amber-500/20">
                    <div className="w-6 h-6 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-3 h-3 text-amber-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-[11px] font-semibold text-foreground truncate">{record.id}</p>
                        <span className="text-[8px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-400 font-semibold">Paper</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground truncate">{MANUAL_PATIENT_LIST_LABELS[record.type]}</p>
                      <p className="text-[10px] text-muted-foreground truncate" title={record.notes}>{record.notes}</p>
                      <p className="text-[9px] text-muted-foreground/70">
                        {record.recordedBy} &bull; {formatTimestamp(record.recordedAt)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Main Content - scrollable */}
        <div className="lg:col-span-2 flex flex-col gap-3 min-h-0">
          {/* Description - fixed height */}
          <div className="glass-panel p-3 flex-shrink-0">
            <p className="text-xs text-muted-foreground leading-relaxed">{incident.description}</p>
            {incident.tags && incident.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {incident.tags.map((tag) => (
                  <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">{tag}</span>
                ))}
              </div>
            )}
          </div>

          {/* Activity Log - scrollable */}
          <div className="glass-panel flex flex-col flex-1 min-h-0">
            <div className="px-3 py-2 border-b border-border flex-shrink-0">
              <h2 className="text-xs font-semibold text-foreground uppercase tracking-wider flex items-center gap-1.5">
                <FileText className="w-3 h-3" /> Activity Log
              </h2>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
              {(incident.logs || []).length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-6">No log entries yet.</p>
              )}
              {(incident.logs || []).map((log) => (
                <div key={log.id} className="flex gap-2">
                  <div className="flex flex-col items-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5" />
                    <div className="w-px flex-1 bg-border mt-1" />
                  </div>
                  <div className="flex-1 pb-2">
                    <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                      <span className="text-[10px] text-muted-foreground font-mono">{formatDateTime(log.timestamp)}</span>
                      <span className="text-xs font-medium text-foreground">{log.userName}</span>
                      <span className="text-[10px] px-1 py-0.5 rounded bg-accent text-muted-foreground">{log.action}</span>
                    </div>
                    <p className="text-sm text-foreground leading-snug">{log.details}</p>
                  </div>
                </div>
              ))}
            </div>
            {/* Add Log Entry - fixed bottom */}
            <div className="px-3 py-2 border-t border-border flex-shrink-0">
              {showShortcodeHelp && (
                <div className="mb-2 p-2 rounded-lg bg-accent/40 border border-border text-[10px] text-foreground space-y-1">
                  <p className="font-semibold mb-1">Shortcodes <span className="text-muted-foreground font-normal">(type prefix/value and press Enter)</span></p>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono">
                    <span><span className="text-primary">AV/</span>callsign — assign unit + dispatch to MDT</span>
                    <span><span className="text-primary">DA/</span>callsign — unassign + cancel MDT</span>
                    <span><span className="text-primary">EN/</span>callsign — mark en route</span>
                    <span><span className="text-primary">OS/</span>callsign — mark on scene</span>
                    <span><span className="text-primary">CL/</span>callsign — clear from incident</span>
                    <span><span className="text-primary">CLOSE/</span>result — close incident</span>
                    <span><span className="text-primary">STATUS/</span>value — set incident status</span>
                    <span><span className="text-primary">COMMANDER/</span>name — set commander</span>
                    <span><span className="text-primary">TOA/</span>callsign — record time of arrival</span>
                    <span><span className="text-primary">PX/MI/</span>notes — minor injury (paper)</span>
                    <span><span className="text-primary">PX/PRF/</span>notes — patient report (paper)</span>
                    <span><span className="text-primary">PX/PAR/</span>notes — paracetamol (paper)</span>
                    <span><span className="text-primary">TAG/</span>word — add a tag</span>
                  </div>
                </div>
              )}
              <form onSubmit={(e) => { e.preventDefault(); handleAddLog(); }} className="flex gap-1.5">
                <button
                  type="button"
                  onClick={() => setShowShortcodeHelp((v) => !v)}
                  title="Show shortcodes"
                  className={cn(
                    'px-2 py-1.5 rounded-lg text-[10px] font-bold border transition-colors',
                    showShortcodeHelp
                      ? 'bg-primary/15 text-primary border-primary/30'
                      : 'bg-background border-border text-muted-foreground hover:text-foreground'
                  )}
                >
                  ?
                </button>
                <input
                  ref={logInputRef}
                  autoFocus
                  value={newLogEntry}
                  onChange={(e) => {
                    setNewLogEntry(e.target.value);
                    // Clear stale pending shortcode confirm if user edits the input
                    if (pendingNewUnitSource === 'shortcode' && e.target.value.trim().toUpperCase() !== `AV/${pendingNewUnit}`) {
                      setPendingNewUnit(null);
                      setPendingNewUnitSource(null);
                    }
                  }}
                  placeholder="Add log entry or shortcode (e.g. AV/MD01)..."
                  className="flex-1 px-2.5 py-1.5 rounded-lg bg-background border border-border text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  disabled={addingLog || eventLocked}
                />
                <button type="submit" disabled={addingLog || eventLocked || !newLogEntry.trim()} className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50">
                  {addingLog ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                </button>
              </form>
              {pendingNewUnitSource === 'shortcode' && pendingNewUnit && (
                <p className="text-[10px] text-amber-400 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Unit <span className="font-mono font-bold">{pendingNewUnit}</span> does not exist. Press Enter again to create &amp; assign.
                </p>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
